<template>
  <FullPage class="inbox" with-find @find="openFindBar">
    <template #topbar-left>
      <Breadcrumb :items="breadcrumbItems" />
    </template>
    <template #topbar-right>
      <InboxFilter />
    </template>
    <div
      id="scrollable-inbox"
      ref="contentEl"
      class="content"
      @contextmenu.prevent
    >
      <FindBar
        v-if="isFindBarOpen"
        ref="findBarEl"
        container-id="scrollable-inbox"
        @close="closeFindBar"
      />
      <BaseNumberedHeader
        v-if="counter > 0 && !isTitleScrolledPast"
        title="Inbox"
        :number="counter"
      />

      <Suspense>
        <!-- The list is normally windowed (2 meetings, 25 items per page);
             while the find bar is open it renders everything so the search
             covers the whole inbox, not just the loaded window. -->
        <InboxList
          class="list"
          :notices="unarchivedNotices"
          :unanswered-meetings="unansweredMeetings"
          :unplanned-tasks="unplannedTasks"
          :untitled-pages="untitledPages"
          :show-all="isFindBarOpen"
        />
        <template #fallback>
          <Loader>
            <BaseSkeletonList />
          </Loader>
        </template>
      </Suspense>
    </div>
  </FullPage>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";

import BaseNumberedHeader from "@/components/base/BaseNumberedHeader.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import Breadcrumb, { type BreadcrumbItem } from "@/components/Breadcrumb.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import InboxFilter from "@/components/inbox/InboxFilter.vue";
import InboxList from "@/components/inbox/InboxList.vue";
import { useInbox } from "@/components/inbox/useInbox";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import FullPage from "@/components/templates/FullPage.vue";
import { useReleaser } from "@/ref";

export default defineComponent({
  name: "Inbox",
  components: {
    Breadcrumb,
    Loader,
    BaseSkeletonList,
    FindBar,
    FullPage,
    InboxList,
    BaseNumberedHeader,
    InboxFilter,
  },
  async setup() {
    const inboxFilter = inject.inboxFilter();
    const releaser = useReleaser();
    const inboxP = releaser.add(useInbox(inboxFilter));

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    const contentEl = ref<HTMLElement>();
    const isTitleScrolledPast = ref(false);

    onMounted(() => {
      const el = contentEl.value;
      if (el) {
        const handler = (e: Event) => {
          const target = e.target;
          isTitleScrolledPast.value =
            target instanceof HTMLElement && target.scrollTop > 0;
        };
        el.addEventListener("scroll", handler, { capture: true });
        releaser.defer(() =>
          el.removeEventListener("scroll", handler, { capture: true })
        );
      }
    });

    const breadcrumbItems = computed((): BreadcrumbItem[] => {
      const item: BreadcrumbItem = {
        title: "Inbox",
        link: { params: { id: "inbox" } },
        fadeIn: true,
        onClick: () => {
          contentEl.value
            ?.querySelector<HTMLElement>(".list")
            ?.scrollTo({ top: 0, behavior: "smooth" });
        },
      };
      return isTitleScrolledPast.value ? [item] : [];
    });

    const {
      counter,
      unarchivedNotices,
      unansweredMeetings,
      unplannedTasks,
      untitledPages,
    } = (await inboxP).value;
    return {
      breadcrumbItems,
      closeFindBar,
      contentEl,
      counter,
      findBarEl,
      isFindBarOpen,
      isTitleScrolledPast,
      openFindBar,
      unarchivedNotices,
      unansweredMeetings,
      unplannedTasks,
      untitledPages,
    };
  },
});
</script>

<style scoped lang="scss">
.inbox {
  .content {
    height: 100%;
    // Anchor for the find bar, which must not scroll with the list.
    position: relative;
  }

  .list {
    overflow-y: scroll;
    height: 100%;
    margin-inline-start: -64px;
    margin-inline-end: -64px;
    margin-bottom: 16px;
  }
}
</style>
